import type { ElementType, ReactNode } from 'react'
import { cn } from '@/lib/utils'

export type StorefrontSectionWidth = 'full' | 'boxed'
export type StorefrontSectionPadding = 'sm' | 'md' | 'lg'
export type StorefrontSectionSurface = 'default' | 'muted' | 'contrast' | 'transparent'

const WIDTH: Record<StorefrontSectionWidth, string> = {
  full: 'w-full max-w-none',
  boxed: 'mx-auto max-w-7xl',
}

const PADDING: Record<StorefrontSectionPadding, string> = {
  sm: 'px-4 py-8 sm:px-6 sm:py-10',
  md: 'px-4 py-10 sm:px-6 sm:py-12 lg:px-8 lg:py-14',
  lg: 'px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16',
}

const SURFACE: Record<StorefrontSectionSurface, string> = {
  default: 'sf-ref-page-surface',
  muted: 'sf-ref-bg-dim-04 sf-ref-text-c',
  contrast: 'sf-ref-elevated-surface ring-1 sf-ref-ring-border-soft',
  transparent: 'bg-transparent sf-ref-text-c',
}

export type StorefrontSectionProps = {
  as?: ElementType
  width?: StorefrontSectionWidth
  padding?: StorefrontSectionPadding
  surface?: StorefrontSectionSurface
  className?: string
  contentClassName?: string
  id?: string
  children?: ReactNode
}

/**
 * Shared storefront section shell — width, vertical rhythm, and surface tokens.
 */
export function StorefrontSection({
  as: Comp = 'section',
  width = 'boxed',
  padding = 'lg',
  surface = 'default',
  className,
  contentClassName,
  id,
  children,
}: StorefrontSectionProps) {
  return (
    <Comp id={id} className={cn(SURFACE[surface], className)}>
      <div className={cn(WIDTH[width], PADDING[padding], contentClassName)}>{children}</div>
    </Comp>
  )
}
